import React, { FC, useRef } from 'react';

import { Box, Divider, Flex, Skeleton, Stack, Text } from '@chakra-ui/react';

import { useQuery } from 'react-query';
import { fetchOrders } from 'api/order';

import { scrollbarStyles } from 'theme';

import { selectors } from 'store/slices/washhub';

import { useSKU } from 'hooks';

import { AnimatePresence, motion } from 'framer-motion';

import {
  EditOrderModal,
  EditOrderModalRef,
} from 'components/modules/EditOrderModal';

import { Order } from 'types/order';
import { Status } from 'types/items';
import { OrderRequestsProps } from './OrderRequests.props';

type Detail = Order['details'][0];

const MotionFlex = motion(Flex);

const getMotionProps = (index: number) => ({
  initial: {
    opacity: 0,
    scale: 0.5,
  },
  animate: {
    opacity: 1,
    scale: 1,
  },
  transition: {
    delay: 0.03 * index,
    ease: 'easeInOut',
    duration: 0.3,
  },
  exit: {
    opacity: 0,
    scale: 0.5,
  },
});

const useOrdersQuery = () => {
  // const [currentWeek] = useCurrentWeek();

  // const startDate = currentWeek[0];
  // const endDate = currentWeek[currentWeek.length - 1];

  const washHubId = selectors.useSelectedWashHubId();

  const queryKey = [`${washHubId}-fetch-orders`];
  const queryFn = async () => {
    const ordersReponse = await fetchOrders({
      // endDate,
      // startDate,
      status: Status.CUSTOMER_REQUEST,
      washHub: washHubId,
    });

    return ordersReponse.items;
  };

  const query = useQuery(queryKey, queryFn);

  return query;
};

const OrderRequestsView: FC<OrderRequestsProps> = (props) => {
  const { data = [], isLoading } = useOrdersQuery();

  const editOrderModalRef = useRef<EditOrderModalRef>(null);

  const isEmpty = data.length === 0;

  const washHubId = selectors.useSelectedWashHubId();

  const { data: skus = [] } = useSKU('package', washHubId);

  const skusMap = skus.reduce((acc, cur) => {
    acc[cur._id] = cur.name;

    return acc;
  }, {} as Record<string, string>);

  const sortDetails = (a: Detail, b: Detail) => {
    const skuA = String(skusMap[a?.sku] || '').toLowerCase();
    const skuB = String(skusMap[b?.sku] || '').toLowerCase();

    return skuA.localeCompare(skuB);
  };

  return (
    <Flex
      p="10px"
      pl="20px"
      minW="300px"
      h="100%"
      bg="#fff"
      minH="480px"
      maxH="480px"
      overflowY="auto"
      css={scrollbarStyles}
      flex={1}
      gap="20px"
      direction="column"
      borderRadius="10px"
      borderWidth="1px">
      <Text fontSize="22px" fontWeight="600" color="primary.200">
        Order Requests ({data.length ?? 0})
      </Text>

      {isLoading ? (
        <>
          <Skeleton height="150px" />
          <Skeleton height="150px" />
          <Skeleton height="150px" />
        </>
      ) : isEmpty ? (
        <Text ml="10px">No Orders for the week</Text>
      ) : (
        <AnimatePresence exitBeforeEnter>
          {data.map((order) => (
            <MotionFlex
              bg="#fff"
              key={order._id}
              boxShadow="0 0 10px 0 rgba(0,0,0,0.1)"
              p="10px"
              pr="20px"
              pb="20px"
              borderRadius="10px"
              direction="column"
              cursor="pointer"
              onClick={() => editOrderModalRef.current?.onOpen?.(order)}
              {...getMotionProps(0)}>
              <Flex gap="10px" align="center">
                <Box h="15px" w="15px" borderRadius="15px" bg="#00BF13" />
                <Text noOfLines={1}>{order.site?.name || ''}</Text>
              </Flex>
              <Text opacity="0.6" ml="25px" fontSize="14px">
                Order No : {order.orderNumber || '-'}
              </Text>
              <Divider my="5px" borderColor="#ccc" />

              <Stack mt="10px" ml="25px" spacing={0} fontSize="14px">
                <Text>Notes: {order.notes || '-'}</Text>
              </Stack>

              <Text
                fontSize="14px"
                mt="10px"
                ml="25px"
                textDecoration="underline">
                Skus
              </Text>
              <Flex
                ml="25px"
                opacity={0.6}
                flex={1}
                fontSize="14px"
                borderBottom="1px solid #ccc">
                <Text flex={1.2}>Name</Text>
                <Text flex={1} textAlign="center">
                  Cases Count
                </Text>
              </Flex>
              <Stack
                ml="25px"
                fontSize="14px"
                spacing={1}
                maxH="100px"
                overflowY="auto"
                css={scrollbarStyles}>
                {order.details?.sort(sortDetails)?.map((detail) => {
                  const sku = skus.find((s) => detail.sku === s._id);

                  return (
                    <Flex
                      key={detail._id}
                      flex={1}
                      borderBottom="1px solid #ccc">
                      <Text flex={1.2} isTruncated>
                        {sku?.name || detail._id}
                      </Text>
                      <Text flex={1} textAlign="center">
                        {detail.casesCount}
                      </Text>
                    </Flex>
                  );
                })}
              </Stack>
            </MotionFlex>
          ))}
        </AnimatePresence>
      )}

      <EditOrderModal
        ref={editOrderModalRef}
        isCustomerRequest
        renderNextOrderButton={() => null}
        renderPreviousOrderButton={() => null}
      />
    </Flex>
  );
};

export default OrderRequestsView;
